import {
  Card,
  Loader,
  ModalComponentWrapper,
  WalletCard,
  UserProfileAction,
} from '@/shared/components';
import { useUserProfileInfo } from '@/entities/user';
import { useSessionStore } from '@/entities/session';
import { useMemo } from 'react';
import VerificationIcon from '@/shared/assets/verification-icon.svg?react';
import UserIcon from '@/shared/assets/user-icon.svg?react';
import LimitsIcon from '@/shared/assets/limits-icon.svg?react';
import DocumentIcon from '@/shared/assets/document-icon.svg?react';
import SecurityIcon from '@/shared/assets/security-icon.svg?react';
import DisplayIcon from '@/shared/assets/display-icon.svg?react';
import ContactBookIcon from '@/shared/assets/contact-book-icon.svg?react';
import HelpIcon from '@/shared/assets/help-icon.svg?react';
import HeartIcon from '@/shared/assets/heart-icon.svg?react';
import LogoutIcon from '@/shared/assets/logout-icon.svg?react';
import { useConfirmationModal } from '@/widgets/confirmation-modal';
import { useTranslation } from 'react-i18next';
import { useUserProfileInfoModal } from '@/widgets/user-profile/info';
import { tv } from 'tailwind-variants';
import { useCommunityModal } from '@/features/view-community';
import {
  DEFAULT_USER_FIRST_NAME,
  DEFAULT_USER_LAST_NAME,
  VERIFICATION_STEPS,
} from '@/shared/config';
import { useAppSettingsDictionary } from '@/shared/api';
import { useSecurityAndPrivacy } from '@/widgets/user-profile/security-and-privacy';
import {
  useLimitsModal,
  useLimits,
  buildTransformedLimitsData,
} from '@/widgets/user-profile/limits';
import { useLegalStatementsModal } from '@/widgets/user-profile/legal-statements';
import { useHelpAndSupportModal } from '@/widgets/user-profile/help-and-support';
import { useMyDocumentsModal } from '@/widgets/user-profile/my-documents';
import { useDisplaySettingsModal } from '@/widgets/user-profile/display-settings';
import {
  formatReceivedAmount,
  useAppSettingsStore,
  useDateLocale,
} from '@/shared/lib';
import { useVerificationModal } from '@/features/user-verification';
import { useVerificationInfo } from '@/features/user-verification/api/queries/useVerificationInfo';
import { useVerificationInfoModal } from '@/entities/verification';
import { useSavedAddressesModal } from '@/widgets/saved-addresses';

interface Props {
  onOpenProfileDialog: () => void;
}

const userProfileClasses = tv({
  slots: {
    titleWrapper: 'flex items-center gap-4',
    contentWrapper: 'flex flex-col gap-[1.6rem]',
    cardWrapper:
      'p-[0.8rem] rounded-[1.6rem] w-full shadow-none flex flex-col gap-2',
    verificationIcon: 'size-10 text-cyan',
    icon: 'size-10',
    logoutIcon: 'text-destructive',
  },
});

export function UserProfileModal({ onOpenProfileDialog }: Props) {
  const { t } = useTranslation();
  const { session, logout } = useSessionStore();
  const { dateLocale } = useDateLocale();
  const { baseAsset } = useAppSettingsStore();

  const isUserVerified = session?.additional?.isVerified || false;
  const ewalletAccNum = session?.additional?.eWallet;

  const {
    titleWrapper,
    contentWrapper,
    cardWrapper,
    icon,
    logoutIcon,
    verificationIcon,
  } = userProfileClasses();

  const { isUserProfileInfoLoading, userProfileInfoMap, userProfileInfoError } =
    useUserProfileInfo();

  const { openModal, Modal: ConfirmationModal } = useConfirmationModal();
  const openProfileDialog = () => onOpenProfileDialog();

  const { openVerificationModal } = useVerificationModal();
  const { openVerificationInfoModal } = useVerificationInfoModal();
  const { userVerificationInfo } = useVerificationInfo(!isUserVerified);
  const isVerificationRejected =
    userVerificationInfo?.stepId === VERIFICATION_STEPS.FINISH &&
    userVerificationInfo?.result === 'rejected';
  const { openUserProfileInfoDialog } = useUserProfileInfoModal();
  const { openCommunityDialog } = useCommunityModal();
  const { openSecurityAndPrivacyModal } = useSecurityAndPrivacy();
  const { openLegalStatementsModal } = useLegalStatementsModal();
  const { openHelpAndSupportModal } = useHelpAndSupportModal();
  const { openMyDocumentsModal } = useMyDocumentsModal();
  const { openLimitsDialog } = useLimitsModal();
  const { data: limitsData, isLoading: isLimitsLoading } = useLimits({
    enabled: isUserVerified,
    baseAsset,
  });
  const { openDisplaySettingsModal } = useDisplaySettingsModal();
  const { openSavedAddressesModal } = useSavedAddressesModal();

  const { appSettingsData, isAppSettingsLoading } = useAppSettingsDictionary({
    enabled: isUserVerified,
  });

  const userFullName = useMemo(() => {
    const hasAnyName =
      userProfileInfoMap?.firstName || userProfileInfoMap?.lastName;

    const firstName = hasAnyName
      ? userProfileInfoMap?.firstName || ''
      : DEFAULT_USER_FIRST_NAME;
    const lastName = hasAnyName
      ? userProfileInfoMap?.lastName || ''
      : DEFAULT_USER_LAST_NAME;

    return `${firstName} ${lastName}`.trim();
  }, [userProfileInfoMap]);

  if (isUserProfileInfoLoading || isLimitsLoading || isAppSettingsLoading) {
    return (
      <ModalComponentWrapper headerClassName="text-secondary-bg bg-secondary-bg/50">
        <div className="p-[0.8rem]">
          <Loader />
        </div>
      </ModalComponentWrapper>
    );
  }

  if (userProfileInfoError) {
    return (
      <ModalComponentWrapper headerClassName="text-secondary-bg bg-secondary-bg/50">
        <div className="p-[0.8rem]">Error</div>
      </ModalComponentWrapper>
    );
  }

  const canShowLimits =
    isUserVerified && Boolean(limitsData) && Boolean(appSettingsData);
  const transformedLimitsMap = buildTransformedLimitsData(
    limitsData,
    appSettingsData?.value.limitsTypes,
    dateLocale
  );

  const getLimitsSubtitle = () => {
    if (!transformedLimitsMap) return '';

    const { limits, baseAsset } = transformedLimitsMap;

    return limits
      .map(({ name, left }) => {
        return t('personalAccount.userProfile.limits.subtitle', {
          name,
          left: formatReceivedAmount(left),
          asset: baseAsset,
        });
      })
      .join('. ');
  };

  const handleLogoutClick = () => {
    openModal({
      title: t('confirmationModal.logout.title'),
      description: t('confirmationModal.logout.description'),
      confirmText: t('confirmationModal.logout.confirmText'),
      cancelText: t('confirmationModal.logout.cancelText'),
      onConfirm: () => logout(),
    });
  };

  const handleOpenAccountInfoDialog = () =>
    openUserProfileInfoDialog({
      onBack: openProfileDialog,
      ewalletAccNum,
    });

  const handleOpenCommunityDialog = () =>
    openCommunityDialog({ onBack: openProfileDialog });

  const handleOpenLimitsDialog = () => {
    if (!transformedLimitsMap) return;

    openLimitsDialog({
      onBack: openProfileDialog,
      walletId: ewalletAccNum || '',
      limitsMap: transformedLimitsMap,
    });
  };

  const handleOpenSecurityAndPrivacyDialog = () =>
    openSecurityAndPrivacyModal({ onBack: openProfileDialog });

  const handleOpenLegalStatementsDialog = () =>
    openLegalStatementsModal({ onBack: openProfileDialog });

  const handleOpenHelpAndSupportDialog = () =>
    openHelpAndSupportModal({
      onBack: openProfileDialog,
      ewalletAccNum,
    });

  const handleOpenMyDocumentsDialog = () =>
    openMyDocumentsModal({ onBack: openProfileDialog, isUserVerified });

  const handleOpenDisplaySettingsDialog = () =>
    openDisplaySettingsModal({ onBack: openProfileDialog });

  const handleOpenSavedAddressesDialog = () =>
    openSavedAddressesModal({ onBack: openProfileDialog });

  const handleOpenVerification = () => {
    if (isVerificationRejected) {
      openVerificationInfoModal({ onBack: openProfileDialog });
      return;
    }

    openVerificationModal();
  };

  return (
    <ModalComponentWrapper
      title={
        <div className={titleWrapper()}>
          {userFullName}
          {isUserVerified ? (
            <VerificationIcon className={verificationIcon()} />
          ) : null}
        </div>
      }
      headerClassName="text-secondary-bg bg-secondary-bg/50">
      <div className={contentWrapper()}>
        {isUserVerified && ewalletAccNum && (
          <WalletCard walletId={ewalletAccNum} />
        )}

        <Card className={cardWrapper()}>
          {!isUserVerified && (
            <UserProfileAction
              icon={<VerificationIcon className={icon()} />}
              title={t('personalAccount.verification.title')}
              subtitle={
                <span className="text-yellow text-left">
                  {isVerificationRejected
                    ? t('personalAccount.verificationCta.completedTitle')
                    : t('personalAccount.verificationCta.processTitle')}
                </span>
              }
              onClick={handleOpenVerification}
            />
          )}

          <UserProfileAction
            icon={<UserIcon className={icon()} />}
            title={t('personalAccount.userProfile.accountInfo.title')}
            onClick={handleOpenAccountInfoDialog}
          />

          {canShowLimits && (
            <UserProfileAction
              icon={<LimitsIcon className={icon()} />}
              title={t('personalAccount.userProfile.limits.title')}
              subtitle={getLimitsSubtitle()}
              onClick={handleOpenLimitsDialog}
            />
          )}
        </Card>

        <Card className={cardWrapper()}>
          <UserProfileAction
            icon={<DocumentIcon className={icon()} />}
            title={t('personalAccount.userProfile.myDocuments.title')}
            onClick={handleOpenMyDocumentsDialog}
          />

          <UserProfileAction
            icon={<SecurityIcon className={icon()} />}
            title={t('personalAccount.userProfile.controls.securityPrivacy')}
            onClick={handleOpenSecurityAndPrivacyDialog}
          />

          <UserProfileAction
            icon={<DisplayIcon className={icon()} />}
            title={t('personalAccount.userProfile.displaySettings.title')}
            onClick={handleOpenDisplaySettingsDialog}
          />

          <UserProfileAction
            icon={<ContactBookIcon className={icon()} />}
            title={t('savedAddresses.title')}
            onClick={handleOpenSavedAddressesDialog}
          />
        </Card>

        <Card className={cardWrapper()}>
          <UserProfileAction
            icon={<HelpIcon className={icon()} />}
            title={t('personalAccount.userProfile.helpAndSupport.title')}
            onClick={handleOpenHelpAndSupportDialog}
          />

          <UserProfileAction
            icon={<DocumentIcon className={icon()} />}
            title={t('personalAccount.userProfile.legalStatements.title')}
            onClick={handleOpenLegalStatementsDialog}
          />

          <UserProfileAction
            icon={<HeartIcon />}
            title={t('personalAccount.userProfile.community.title')}
            onClick={handleOpenCommunityDialog}
          />
        </Card>

        <Card className={cardWrapper()}>
          <UserProfileAction
            showChevron={false}
            icon={<LogoutIcon className={logoutIcon()} />}
            title={
              <span className="text-destructive">
                {t('personalAccount.userProfile.controls.logout')}
              </span>
            }
            onClick={handleLogoutClick}
          />
        </Card>
      </div>

      {ConfirmationModal}
    </ModalComponentWrapper>
  );
}
